import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export interface InventoryItem {
  id: string;
  ingredient_id: string;
  ingredient_name: string;
  transaction_type: 'purchase' | 'usage' | 'adjustment' | 'waste';
  quantity: number;
  unit_price: number | null;
  transaction_date: string;
  notes: string | null;
  reference_id: string | null;
  created_at: string;
}

export interface InventorySummary {
  totalItems: number;
  lowStockCount: number;
  totalValue: number;
  recentTransactions: InventoryItem[];
}

export function useInventory() {
  const { user } = useAuth();
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [summary, setSummary] = useState<InventorySummary>({
    totalItems: 0,
    lowStockCount: 0,
    totalValue: 0,
    recentTransactions: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchInventoryData();
    }
  }, [user]);

  const fetchInventoryData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch recent inventory transactions
      const { data: transactions, error: transactionsError } = await supabase
        .from('ingredients_inventory')
        .select(`
          id,
          ingredient_id,
          ingredients (name),
          transaction_type,
          quantity,
          unit_price,
          transaction_date,
          notes,
          reference_id,
          created_at
        `)
        .order('transaction_date', { ascending: false })
        .limit(10);

      if (transactionsError) throw transactionsError;

      // Fetch low stock count
      const { data: lowStockData, error: lowStockError } = await supabase
        .rpc('check_low_stock_ingredients');

      if (lowStockError) throw lowStockError;

      // Fetch total ingredients count
      const { count: totalItems, error: countError } = await supabase
        .from('ingredients')
        .select('*', { count: 'exact', head: true });

      if (countError) throw countError;

      // Calculate total inventory value
      const { data: valueData, error: valueError } = await supabase
        .from('ingredients')
        .select('current_stock, preco_standard');

      if (valueError) throw valueError;

      const totalValue = valueData?.reduce((sum, item) => {
        return sum + (item.current_stock * item.preco_standard);
      }, 0) || 0;

      // Format transactions data
      const formattedTransactions = transactions?.map(item => ({
        id: item.id,
        ingredient_id: item.ingredient_id,
        ingredient_name: item.ingredients?.name || 'Desconhecido',
        transaction_type: item.transaction_type as 'purchase' | 'usage' | 'adjustment' | 'waste',
        quantity: item.quantity,
        unit_price: item.unit_price,
        transaction_date: item.transaction_date,
        notes: item.notes,
        reference_id: item.reference_id,
        created_at: item.created_at
      })) || [];

      setInventoryItems(formattedTransactions);
      setSummary({
        totalItems: totalItems || 0,
        lowStockCount: lowStockData?.length || 0,
        totalValue,
        recentTransactions: formattedTransactions
      });
    } catch (err: any) {
      console.error('Error fetching inventory data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addInventoryTransaction = async (
    ingredient_id: string,
    transaction_type: 'purchase' | 'usage' | 'adjustment' | 'waste',
    quantity: number,
    unit_price?: number,
    notes?: string,
    reference_id?: string
  ) => {
    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('ingredients_inventory')
        .insert([
          {
            ingredient_id,
            transaction_type,
            quantity,
            unit_price: unit_price || null,
            notes: notes || null,
            reference_id: reference_id || null,
            created_by: user.id
          }
        ])
        .select();

      if (error) throw error;

      // Refresh data
      await fetchInventoryData();
      
      toast.success(`Transação de ${transaction_type} registrada com sucesso`);
      return { data, error: null };
    } catch (err: any) {
      console.error('Error adding inventory transaction:', err);
      toast.error(`Erro ao registrar transação: ${err.message}`);
      return { data: null, error: err.message };
    }
  };

  const getLowStockIngredients = async () => {
    try {
      const { data, error } = await supabase
        .rpc('check_low_stock_ingredients');

      if (error) throw error;
      
      return data || [];
    } catch (err: any) {
      console.error('Error fetching low stock ingredients:', err);
      return [];
    }
  };

  const getIngredientUsage = async (
    ingredient_id: string,
    days: number = 30
  ) => {
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      
      const { data, error } = await supabase
        .rpc('calculate_ingredient_usage', {
          p_ingredient_id: ingredient_id,
          p_start_date: startDate.toISOString(),
          p_end_date: endDate.toISOString()
        });

      if (error) throw error;
      
      return data || { total_usage: 0, average_daily_usage: 0, estimated_days_remaining: null };
    } catch (err: any) {
      console.error('Error calculating ingredient usage:', err);
      return { total_usage: 0, average_daily_usage: 0, estimated_days_remaining: null };
    }
  };

  return {
    inventoryItems,
    summary,
    loading,
    error,
    fetchInventoryData,
    addInventoryTransaction,
    getLowStockIngredients,
    getIngredientUsage
  };
}